'use client';

import { PropsWithChildren, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { AlertCode } from 'shared/types/checkout';
import { authenticateUser } from 'app/actions';
import { makeSDKCallWithRetries } from 'helpers/makeSDKCallWithRetries';
import { isCookieValid, loginRedirectUrl } from 'helpers/utils/cookie';
import {
  getAccountId,
  getIsAuthenticated,
  setAccountId,
  setAlert,
  setIsAuthenticated,
  useAppDispatch,
  useAppSelector,
} from 'store';
import { AuthenticationProviderProps } from './types';

const PUBLIC_ROUTES = ['redeem-discount/'];

export const AuthenticationProvider = ({
  children,
  locale,
}: PropsWithChildren<AuthenticationProviderProps>) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const router = useRouter();
  const pathname = usePathname();
  const customerId = useAppSelector(getAccountId);

  const isPublic = PUBLIC_ROUTES.some((route) => pathname.endsWith(route));

  useEffect(() => {
    const establishFrontasticSession = async () => {
      if (isPublic) {
        return;
      }
      // only do login refresh if there is less than 30 mins to expiration
      if (!isCookieValid(30 * 60)) {
        const loginRefreshResponse = await fetch(
          process.env.NEXT_PUBLIC_EDX_LOGIN_REFRESH_ENDPOINT!,
          {
            method: 'POST',
            credentials: 'include',
          },
        );

        if (!loginRefreshResponse.ok) {
          router.replace(loginRedirectUrl());
          return;
        }
      }
      const accountResult = await makeSDKCallWithRetries(
        'getAccount',
        customerId,
        () => authenticateUser(locale),
      );
      if (!accountResult || accountResult.isError) {
        console.error(
          `Error fetching account information with error message: ${
            accountResult.error.message ?? ''
          }, name: ${accountResult.error.name ?? ''}, stack: ${
            accountResult.error.stack ?? ''
          }`,
        );
        dispatch(
          setAlert({ code: AlertCode.FALLBACK_WITH_RELOAD_INFORMATION }),
        );
        return;
      }
      if (!accountResult.data.isError && accountResult.data.loggedIn) {
        dispatch(setIsAuthenticated(true));
        if (!customerId && accountResult.data.account?.accountId) {
          dispatch(setAccountId(accountResult.data.account.accountId));
        }
      } else {
        router.replace(loginRedirectUrl());
      }
    };

    establishFrontasticSession().catch((e: Error) => {
      console.error(
        `Error establishing Frontastic session with error message: ${
          e.message ?? ''
        }, name: ${e.name ?? ''}, stack: ${e.stack ?? ''}`,
      );
      dispatch(setAlert({ code: AlertCode.FALLBACK_WITH_RELOAD_INFORMATION }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  if (isPublic) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }
  if (isAuthenticated) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return null;
};
